import { useMemo } from 'react';
import { VaultDepositor } from '@velocity-exchange/vaults-sdk';
import { useVaultDepositorAccounts } from 'src/hooks/query/vaults/base/useVaultDepositorAccounts';
import {
	useAllVaultStats,
	VaultStatWithConfig,
} from 'src/hooks/query/vaults/derived/useVaultStats';

export type VaultStatsWithDepositor = {
	vaultStat: VaultStatWithConfig;
	depositor: {
		vaultDepositor: VaultDepositor;
	} | null;
};

/**
 * Hook that combines vault stats with their corresponding depositor accounts.
 * This avoids having to loop through depositor accounts every time we need to match them with vault stats.
 * @returns An array of vault stats with their matched depositor accounts (null if no match)
 */
export const useVaultStatsWithDepositors = () => {
	const { data } = useAllVaultStats();
	const vaultsStatsList = data.list;
	const { data: authorityDepositedVaults } = useVaultDepositorAccounts();

	return useMemo(() => {
		// Create a map of vault pubkey to depositor for faster lookups
		const depositorMap = new Map<
			string,
			{
				vaultDepositor: VaultDepositor;
			}
		>();
		Object.values(authorityDepositedVaults ?? {}).forEach((vault) => {
			depositorMap.set(vault.vaultDepositor.vault.toString(), {
				vaultDepositor: vault.vaultDepositor,
			});
		});

		// Combine vault stats with their depositors
		return vaultsStatsList.map((vaultStat): VaultStatsWithDepositor => {
			const vaultPubkey = vaultStat.uiVaultConfig.vaultPubkeyString;
			const depositor = depositorMap.get(vaultPubkey) || null;

			return {
				vaultStat,
				depositor,
			};
		});
	}, [vaultsStatsList, authorityDepositedVaults]);
};
